import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const PRINTER_SVG = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="2" y="5" width="10" height="7" rx="1" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M4 5V3a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="1.4"/>
    <rect x="4" y="8" width="6" height="1" rx=".5" fill="currentColor"/>
    <rect x="4" y="10" width="4" height="1" rx=".5" fill="currentColor"/>
  </svg>
);

function fmtTime(t) {
  if (!t || !/^\d{1,2}:\d{2}/.test(String(t))) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Print the team's Conference Schedule by day: pick a day (or all days),
// each day is a clean table of time / what / where, with team-item
// descriptions underneath. Mirrors PrintPreviewModal's plumbing: rendered
// through a PORTAL as a direct <body> child because the print stylesheet
// hides `body > *` and re-shows `.modal-overlay` — buried inside the app
// root the printout would be blank.
export default function PrintScheduleModal({ open, onClose, groups = [], teamName }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && open) onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const [selDay, setSelDay] = useState('__all');
  useEffect(() => {
    if (open) setSelDay(prev => (prev === '__all' || groups.some(g => g.day === prev)) ? prev : '__all');
  }, [open, groups]);

  if (!open) return null;

  const printGroups = selDay === '__all' ? groups : groups.filter(g => g.day === selDay);
  const itemCount = printGroups.reduce((n, g) => n + g.items.length, 0);
  const generated = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return createPortal(
    <div
      className="modal-overlay open"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', width: '95vw', maxWidth: '9.5in', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{teamName} — Print Schedule</div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>Preview · 8.5 × 11 in · the schedule exactly as filtered on screen</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <select
              className="form-input"
              style={{ width: 'auto', fontSize: 13, padding: '6px 10px', height: 'auto' }}
              value={selDay}
              onChange={e => setSelDay(e.target.value)}
            >
              <option value="__all">All days</option>
              {groups.map(g => (
                <option key={g.day} value={g.day}>{g.day} ({g.items.length} item{g.items.length === 1 ? '' : 's'})</option>
              ))}
            </select>
            <button className="btn btn-sm btn-dark" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => window.print()}>
              {PRINTER_SVG} Print / Save PDF
            </button>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="print-scroll-region" style={{ background: '#e0e0e0', padding: 24, overflowY: 'auto', flex: 1 }}>
          <div className="print-preview-page">
            <div className="print-preview-header">{teamName} — Conference Schedule</div>
            <div className="print-preview-sub">
              {selDay === '__all' ? 'All days' : selDay} · Belonging Conference 2026 · Generated {generated}
            </div>

            {itemCount === 0 && (
              <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Nothing to print for this day.</div>
            )}

            {printGroups.map(group => (
              <div key={group.day} className="print-day-group">
                <div className="print-day-heading">{group.day}</div>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '1.4in' }}>Time</th>
                      <th>Activity</th>
                      <th style={{ width: '1.9in' }}>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item, i) => (
                      <tr key={i}>
                        <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {fmtTime(item.start) || '—'}
                          {item.end && fmtTime(item.end) && (
                            <div style={{ fontFamily: 'var(--font-b)', fontSize: 11, fontWeight: 400, color: '#555' }}>– {fmtTime(item.end)}</div>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          <span style={{ fontFamily: 'var(--font-h)', fontWeight: 700, fontSize: 13 }}>{item.activity}</span>
                          {item.linked && item.linked.length > 0 && (
                            <span style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, color: '#555' }}> · {item.linked.join(', ')}</span>
                          )}
                          {item.description && (
                            <div style={{ fontFamily: 'var(--font-b)', fontSize: 11.5, fontWeight: 400, color: '#444', marginTop: 3, whiteSpace: 'pre-wrap' }}>{item.description}</div>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'top', fontFamily: 'var(--font-b)', fontSize: 12.5 }}>{item.location || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
